import React, { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Redirect } from 'react-router'
import { Formik, Form } from 'formik'
import { useMsal } from '@azure/msal-react'

import { Button, Grid, H, Wrapper } from '@farewill/ui'
import { GTR } from '@farewill/ui/tokens'

import InputFloating from 'components/form/input-floating'
import ENV from 'config/environment'
import { login } from 'state/actions'

const isReviewApp = ENV.APP_ENV === 'review'
const isTestApp = ENV.APP_ENV === 'test'

/** mirrors E2E_TEST_PASSWORD in server/src/shared/api/access-tokens/methods.ts */
const E2E_TEST_PASSWORD = 'e2e-test'

const Login = (): React.ReactElement => {
  const usePassword = isReviewApp || isTestApp
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { instance: microsoftInstance } = useMsal()
  const dispatch = useDispatch()
  const token = useSelector((state: { token: string }) => state.token)
  const loginPending = useSelector((state: { requestsPending: string[] }) =>
    state.requestsPending.includes('LOGIN')
  )

  const MicrosoftLogInButton = () => {
    return (
      <Button.Primary
        type="button"
        onClick={async () => {
          let response
          try {
            response = await microsoftInstance.loginPopup()
          } catch (error) {
            console.error('Microsoft login error', error)
            return
          }

          const microsoftIdToken = response.idToken
          handleLogin({ microsoftIdToken })
        }}
      >
        Log In
      </Button.Primary>
    )
  }

  const handleLogin = useCallback(
    async ({
      microsoftIdToken,
      password,
    }: {
      microsoftIdToken?: string
      password?: string
    }): Promise<void> => {
      try {
        await dispatch(login({ microsoftIdToken, password }))
      } catch (error) {
        console.error(error)
      }
    },
    [dispatch]
  )

  if (token && !loginPending) return <Redirect to="/leads" />

  if (usePassword) {
    return (
      <Grid padding={[GTR.XL, 0, 0]}>
        <Grid.Item spanFromM={4} startColumnFromM={5}>
          <H size="M">Log in to Backstage</H>
        </Grid.Item>
        <Grid.Item spanFromM={4} startColumnFromM={5}>
          <Formik
            initialValues={{
              password: isTestApp ? E2E_TEST_PASSWORD : '',
              credential: undefined,
              microsoftIdToken: undefined,
            }}
            onSubmit={handleLogin}
          >
            <Form>
              {isReviewApp && (
                <Wrapper margin={[0, 0, 'L']}>
                  <InputFloating
                    label="Password"
                    name="password"
                    type="password"
                    hint="This saved in 1Password as ‘Backstage Review Apps’"
                  />
                </Wrapper>
              )}
              <Button.Primary type="submit">
                {isTestApp ? 'Log in' : 'Submit'}
              </Button.Primary>
            </Form>
          </Formik>
        </Grid.Item>
      </Grid>
    )
  }

  return (
    <Grid padding={[GTR.XL, 0, 0]}>
      <Grid.Item spanFromM={4} startColumnFromM={5}>
        <H size="M">Log in with Microsoft</H>
        <MicrosoftLogInButton />
      </Grid.Item>
    </Grid>
  )
}

export default Login
